<!--
  The page for viewing and editing scripts
-->

<template>
  <el-form
    ref="form"
    v-loading="state.loading"
    :model="viewObj"
    label-width="120px"
    :hide-required-asterisk="true">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item>
        <router-link :to="{ name: 'home', query: {view: view, compare: compare} }">
          Home
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item v-if="skill==='global'">
        <router-link :to="{ name: 'global', query: {view: view, compare: compare} }">
          Global items
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item v-else>
        <router-link :to="{ name: 'skill', params: {skill: skill}, query: {view: view, compare: compare} }">
          Skill summary
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item>{{scriptId==='new'?'Create script':'Script details'}}</el-breadcrumb-item>
    </el-breadcrumb>
    <main-title
      v-model="viewObj"
      id-field="script"
      :object-id="scriptId"
      :existing="skillObj.data.scripts">
      Script: {{viewObj.module}}.{{viewObj.function}}
    </main-title>
    <view-selector
      :view="view"
      :compare="compare"
      @update="changeView"
      :object="viewObj"
      :publishable="skill === 'global'"
      :discrete="skill !== 'global' && skillObj.data.discrete_publish"
      :new-item="scriptId === 'new'"
      name="script"
      @save="save"
      @delete="remove"
      @test="test"
      @untest="untest"
      @publish="publish"
      @unpublish="unpublish"
      @unprod="unprod"
      @reject="reject"
      @approve="approve"
      @disapprove="disapprove"
      @restore="restore"
      ></view-selector>
    <!-- name inputs -->
    <p>
      <comparator :view="viewObj" :compare="compareObj"
        prop="module"
        :required="true"
        :rules="VariableValidator">
        <template v-slot="{ value, compare, prop, attrs, status }">
          <el-form-item :class="'comparable-' + status"
            :prop="prop"
            v-bind="attrs"
            label="Module">
            <el-input v-model="value.module"
              :disabled="compare"></el-input>
          </el-form-item>
        </template>
      </comparator>
      <comparator :view="viewObj" :compare="compareObj"
        prop="function"
        :required="true"
        :rules="VariableValidator">
        <template v-slot="{ value, compare, prop, attrs, status }">
          <el-form-item :class="'comparable-' + status"
            :prop="prop"
            v-bind="attrs"
            label="Function">
            <el-input v-model="value.function"
              :disabled="compare"></el-input>
          </el-form-item>
        </template>
      </comparator>
    </p>
    <!-- docstring -->
    <p>
      <el-collapsable title="Docstring format" v-model="format"
          style="margin-left: 120px">
        <h3>Format</h3>
        <p>
          The first line is a description of how the funtion should be called.
        </p>
        <figure>
          <code>
            (type1 arg1, type2 arg2="default" [, type3 arg3, [, ...]]) type
          </code>
        </figure>
        <p>
          Then comes any number of description lines. Use <code>`code`</code> (<code>code</code>) for types and code, and <code>*bold*</code> (<strong>bold</strong>) for variables.
        </p>
        <p>
          Several calls can be described, for diffent ways to call the function. Calls can be prefixed with <code>!Module!</code> or <code>!Prototype!</code>, if a call only works on the module (<code>MyModule.my_function()</code>) or as a prototype method (<code>value.my_method()</code>).
        </p>
        <h3>Simple example</h3>
        <figure>
          <code>
            (number n1, number n2) number<br/>
            Adds 2 `numbers` *n1* and *n2*.
          </code>
        </figure>
        <h3>Multiple usage</h3>
        <figure>
          <code>
            (number n1, number n2, [, number n3, [, ...]]) number<br/>
            Returns the sum of the given `numbers`.<br/>
            <br/>
            (array n) number<br/>
            Returns the sum of the given `array` of `numbers` *n*.<br/>
            Returns `0` if the `array` *n* is empty.
          </code>
        </figure>
      </el-collapsable>
      <comparator :view="viewObj" :compare="compareObj"
        prop="docstring"
        :required="true">
        <template v-slot="{ value, compare, prop, attrs, status }">
          <el-form-item :class="'comparable-' + status"
            :prop="prop"
            v-bind="attrs"
            label="Docstring">
            <el-input v-model="value.docstring"
              type="textarea"
              :rows="10"
              :disabled="compare"></el-input>
          </el-form-item>
        </template>
      </comparator>
    </p>
    <!-- source -->
    <p>
      <comparator :view="viewObj" :compare="compareObj"
        prop="source"
        :required="true">
        <template v-slot="{ value, compare, prop, attrs, status }">
          <el-form-item :class="'comparable-' + status"
            :prop="prop"
            v-bind="attrs"
            label="Source">
            <el-input v-model="value.source"
              type="textarea"
              :rows="50"
              :disabled="compare"></el-input>
          </el-form-item>
        </template>
      </comparator>
    </p>
  </el-form>
</template>

<script>
import State from '../../stores/state.js'
import { VariableValidator } from '../../utils/validators'
import ViewSelector from '../ViewSelector.vue'
import ElCollapsable from '../element/ElCollapsable.vue'

export default {
  name: 'ScriptPage',
  components: {
    ViewSelector,
    ElCollapsable,
  },
  props: {
    skill: String,
    script: String,
    view: String,
    compare: String,
  },
  data () {
    return {
      state: State.state,
      scriptId: null, // "new" for a new script, then updated on creation
      skillObj: State.empty,
      format: false,
      viewObj: {
        module: '',
        function: '',
        docstring: '',
        source: '',
      },
      compareObj: undefined,
      VariableValidator,
    }
  },
  inject: ['onerror'],
  // load data
  created () {
    this.scriptId = this.script
    Promise.all([
      this.loadSkill(),
      this.loadView(),
      this.loadCompare(),
    ]).finally(() => {
      this.$anchorsLoaded()
    })
  },
  computed: {
    // shortcuts
    status () { return this.viewObj.status || null },
    timestamp () { return this.viewObj.timestamp || null },
    published () { return this.viewObj.published || null },
    approved () { return this.viewObj.approved || null },
  },
  watch: {
    // user connected, reload everything
    'state.user' () {
      this.loadSkill()
      this.loadView()
      this.loadCompare()
    },
    // skill changed, reload everything
    skill () {
      this.loadSkill()
      this.loadView()
      this.loadCompare()
    },
    // script change, reload script data
    script () {
      if (this.scriptId === this.script) return
      this.scriptId = this.script
      this.loadView()
      this.loadCompare()
    },
    // view change, reload view data
    view () {
      this.loadSkill()
      this.loadView()
    },
    // compare change, reload compare data
    compare () { this.loadCompare() },
  },
  methods: {
    // load skill data
    loadSkill () {
      let skill = this.skill, view = this.view
      return State.getSkill(skill, view).then(data => {
        if (skill !== this.skill || view !== this.view) return
        this.skillObj = data
      }).catch(this.onerror)
    },
    // load view script data
    loadView () {
      if (this.scriptId === 'new') {
        this.viewObj = {
          module: '',
          function: '',
          docstring: '',
          source: '',
        }
        return Promise.resolve()
      } else {
        let skill = this.skill, script = this.scriptId, view = this.view
        return State.getScript(skill, script, view).then(data => {
          if (skill !== this.skill || script !== this.scriptId ||
            view !== this.view) return
          this.viewObj = data
        }).catch(onerror)
      }
    },
    // load compare script data
    loadCompare () {
      if (this.scriptId === 'new' || !this.compare) {
        this.compareObj = undefined
        return Promise.resolve()
      } else {
        let skill = this.skill, script = this.scriptId, view = this.compare
        return State.getScript(skill, script, view).then(data => {
          if (skill !== this.skill || script !== this.scriptId ||
            view !== this.compare) return
          this.compareObj = data
        }).catch(onerror)
      }
    },
    // delete script
    remove () {
      State.removeScript(this.skill, this.scriptId).then(() => {
        this.$router.replace('/skills/' + this.skill +
          '/scripts/new')
      }).catch(this.onerror)
    },
    // save script
    save () {
      this.$refs.form.validate((valid, errors) => {
        if (valid) {
          this.loading = true
          let promise
          if (this.scriptId === 'new') {
            promise = State.createScript(this.skill, this.viewObj, this.viewObj.script)
              .then(data => {
                this.$message({
                  message: 'Script created',
                  type: 'success',
                })
                this.scriptId = data.script
                this.viewObj = data
                this.$router.replace('/skills/' + this.skill +
                '/scripts/' + this.scriptId)
              })
          } else {
            promise = State.saveScript(this.skill, this.scriptId, this.viewObj)
              .then(data => {
                this.$message({
                  message: 'Script saved',
                  type: 'success',
                })
                this.viewObj = data
              })
          }
          promise.catch(this.onerror).finally(() => { this.loading = false })
        } else {
          console.error(errors)
          this.$message.error('Please correct the errors and try again')
        }
      })
    },
    // untest script
    untest () {
      State.untestScript(this.skill, this.scriptId).then(() => {
        this.$message({
          message: 'Test cleared',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // test script
    test () {
      State.testScript(this.skill, this.scriptId).then(() => {
        this.$message({
          message: 'Script in test',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // update script data with the field from ppublished, ...
    updateScript (data) {
      if (!data || !data.script) return
      for (let field in data) this.viewObj[field] = data[field]
    },
    // unpublish script
    unpublish () {
      State.unpublishScript(this.skill, this.scriptId, this.published).then(data => {
        this.updateScript(data)
        this.$message({
          message: 'Script unpublished',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // unprod script
    unprod () {
      State.publishScript(this.skill, this.scriptId, false).then(data => {
        this.updateScript(data)
        this.$message({
          message: 'Script unproded',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // publish script
    publish () {
      State.publishScript(this.skill, this.scriptId, this.timestamp).then(data => {
        this.updateScript(data)
        this.$message({
          message: 'Script published',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // disapprove script
    disapprove () {
      State.disapproveScript(this.skill, this.scriptId, this.approved).then(data => {
        this.updateScript(data)
        this.$message({
          message: 'Script disapproved',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // reject script
    reject () {
      State.rejectScript(this.skill, this.scriptId, this.published).then(data => {
        this.updateScript(data)
        this.$message({
          message: 'Script rejected',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // approve script
    approve () {
      State.approveScript(this.skill, this.scriptId, this.published).then(data => {
        this.updateScript(data)
        this.$message({
          message: data.published ? 'Script approved' : 'Deletion approved',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // restore script
    restore (version) {
      let timestamp = version === 'pub' ? this.published : this.approved
      let name = version === 'pub' ? 'Published' : 'Approved'
      console.log('restore', version, timestamp, name)
      State['restore' + name + 'Script'](this.skill, this.scriptId, timestamp).then(() => {
        this.$message({
          message: name + ' script restored',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // update view or compare
    changeView (view, compare) {
      this.$router.push({ query: { view: view, compare: compare } })
    },
  },
}
</script>
